/**
 * A proposal as the client sees it — no account, no login, just the link.
 *
 * This renders `DocumentView` and `PagedFrame`, the very same components the
 * operator's Preview tab uses, from the very same `resolveDraft` figures. That
 * is the whole point of reaching for them rather than writing a read-only
 * renderer here: a second renderer would drift, and the first time anyone
 * noticed would be a client asking why their copy says something different.
 * The page breaks, the branding, the tables and the PDF are therefore
 * identical by construction, not by review. The Word download is the one thing
 * here that is rebuilt rather than reused — it has to be, and `docx.ts` says
 * why — so it reads its figures from the same `resolveDraft` map the column
 * above it is drawn from.
 *
 * `?download=1` starts the PDF as soon as the document has settled, and
 * `?download=docx` the Word file — those are the links an assistant hands back
 * over MCP, so "here is the proposal" is one click rather than two.
 *
 * Mounted before the authenticated shell in `App.tsx`, beside the two portals,
 * because the reader has no `/api/me` row to fetch and no company to be in.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ProseDoc } from "./RichText";
import { rateToPercent } from "./api";
import {
  type CompanyIdentity,
  EMPTY_IDENTITY,
  brandPalette,
  brandTypography,
  useBrandFonts,
} from "./brand";
import { downloadProposalDocx } from "./docx";
import { paginate, samePages } from "./paged";
import { DocumentView, PagedFrame, type SectionDraft, exportName } from "./pages/ContractDetail";
import { type SegmentDraft, newKey, resolveDraft } from "./pages/contract-parts";
import { downloadProposalPdf } from "./pdf";
import { Button } from "./ui";

/** The payload `GET /api/documents/:token` returns. Typed by hand rather than
 *  through the RPC client: this route is deliberately outside the
 *  company-scoped tree the client's types are generated from. */
interface DocumentPayload {
  contract: {
    id: string;
    number: string;
    title: string;
    kind: "proposal" | "contract";
    currency: string;
    tagline: string | null;
    projectType: string | null;
    projectLocation: string | null;
    preparedBy: string | null;
    clientContactName: string | null;
    clientPhone: string | null;
    clientEmail: string | null;
    issuedOn: string | null;
    validThrough: string | null;
    acceptedByName: string | null;
    acceptedAt: string | null;
  };
  client: { id: string; name: string } | null;
  company: CompanyIdentity | null;
  segments: {
    id: string;
    name: string;
    projectCode: string | null;
    retainagePct: string;
    startDate: string | null;
    targetEndDate: string | null;
    projectId: string | null;
    items: {
      id: string;
      itemNo: string | null;
      description: string;
      quantity: string;
      amount: string;
      estimateLineId: string | null;
    }[];
    schedule: {
      id: string;
      name: string;
      basis: "percent" | "amount";
      percent: string | null;
      amount: string | null;
      plannedDate: string | null;
    }[];
  }[];
  sections: {
    kind: string;
    title: string | null;
    body: unknown;
    segmentIds: string[];
  }[];
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-100 px-6 text-center text-neutral-600">
      <div className="max-w-md">{children}</div>
    </div>
  );
}

export function DocumentApp() {
  const token = window.location.pathname.replace(/^\/d\//, "").split("/")[0] ?? "";
  /** `?download=1` is the PDF, and stays that way — links handed out before
   *  Word existed have to keep meaning what they meant. `?download=docx` is
   *  the Word file. */
  const wantsDownload = new URLSearchParams(window.location.search).get("download");
  const autoFormat = wantsDownload === "docx" ? "docx" : wantsDownload === "1" ? "pdf" : null;

  const docRef = useRef<HTMLElement>(null);
  const [pageTops, setPageTops] = useState<number[]>([0]);
  const [downloaded, setDownloaded] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingDocx, setExportingDocx] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["document", token],
    // No auth header and no cookie: the token in the path is the whole
    // authorisation, which is what lets this be sent to someone with no
    // account.
    queryFn: async (): Promise<DocumentPayload> => {
      const res = await fetch(`/api/documents/${encodeURIComponent(token)}`);
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? "This link is no longer available.");
      }
      return (await res.json()) as DocumentPayload;
    },
    retry: false,
  });

  const data = q.data;
  const identity: CompanyIdentity = data?.company ?? EMPTY_IDENTITY;
  const palette = brandPalette(identity.brandColor);
  const typography = useMemo(() => brandTypography(identity), [identity]);
  const fontsReady = useBrandFonts(typography);

  /** The server's rows in the shape `DocumentView` reads — the same mapping
   *  the editor does when it loads a contract. */
  const { segments, sections } = useMemo(() => {
    if (!data) return { segments: [] as SegmentDraft[], sections: [] as SectionDraft[] };

    const segs: SegmentDraft[] = data.segments.map((seg) => ({
      key: newKey(),
      id: seg.id,
      name: seg.name,
      projectCode: seg.projectCode ?? "",
      retainagePercent: seg.retainagePct === "0.000000" ? "" : rateToPercent(seg.retainagePct),
      startDate: seg.startDate ?? "",
      targetEndDate: seg.targetEndDate ?? "",
      projectId: seg.projectId,
      items: seg.items.map((i) => ({
        key: newKey(),
        itemNo: i.itemNo ?? "",
        description: i.description,
        amount: i.amount,
        estimateLineId: i.estimateLineId,
      })),
      schedule: seg.schedule.map((r) => ({
        key: newKey(),
        name: r.name,
        basis: r.basis,
        percent: r.percent ? rateToPercent(r.percent) : "",
        amount: r.amount ?? "",
        plannedDate: r.plannedDate ?? "",
      })),
    }));

    const keyById = new Map(segs.map((s) => [s.id, s.key]));
    return {
      segments: segs,
      sections: data.sections.map((sec) => ({
        key: newKey(),
        kind: sec.kind as SectionDraft["kind"],
        title: sec.title ?? "",
        body: (sec.body as ProseDoc | null) ?? null,
        segmentKeys: sec.segmentIds
          .map((id) => keyById.get(id))
          .filter((k): k is number => k !== undefined),
      })),
    };
  }, [data]);

  const resolvedByKey = useMemo(
    () => new Map(segments.map((seg) => [seg.key, resolveDraft(seg)])),
    [segments],
  );

  /**
   * Lay the document onto sheets. A layout effect so the whitespace is in
   * place before paint — a frame of the unpaginated column reads as a flash —
   * and an observer besides, for the brand face arriving or the window
   * narrowing, which no dependency list can see.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure on content, not only on size
  useLayoutEffect(() => {
    const root = docRef.current;
    if (!root) return;

    let frame = 0;
    const run = () => {
      const next = paginate(root);
      setPageTops((cur) => (samePages(cur, next) ? cur : next));
    };
    run();

    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(run);
    });
    observer.observe(root);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [sections, segments, fontsReady]);

  function download() {
    const doc = docRef.current;
    if (!doc || !data) return;
    setExporting(true);
    downloadProposalPdf(doc, `${exportName(data.contract)}.pdf`, () => paginate(doc))
      .catch((err) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setExporting(false));
  }

  /** No wait on `fontsReady` and no reach for `docRef`: the Word file is built
   *  from the payload, not from the laid-out column, so it is ready the moment
   *  the document has loaded. */
  function downloadDocx() {
    if (!data) return;
    setExportingDocx(true);
    downloadProposalDocx(
      {
        kind: data.contract.kind,
        contract: data.contract,
        clientName: data.client?.name ?? "",
        identity,
        palette,
        sections,
        segments,
        resolvedByKey,
      },
      `${exportName(data.contract)}.docx`,
    )
      .catch((err) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setExportingDocx(false));
  }

  /**
   * The automatic download fires once.
   *
   * The PDF waits on the fonts and on the mounted column: rasterizing before
   * a face has loaded produces a file in the fallback typeface that still
   * looks finished. Word has nothing to wait for beyond the payload, since it
   * builds from the rows rather than from the page.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: fires once, guarded by `downloaded`
  useEffect(() => {
    if (!autoFormat || downloaded || !data) return;
    if (autoFormat === "docx") {
      setDownloaded(true);
      downloadDocx();
      return;
    }
    if (!fontsReady || !docRef.current) return;
    setDownloaded(true);
    download();
  }, [autoFormat, downloaded, data, fontsReady]);

  if (q.isPending) return <Centered>Loading…</Centered>;

  if (q.isError) {
    return (
      <Centered>
        <h1 className="mb-2 font-[family-name:--font-display] text-xl font-semibold text-neutral-900">
          This link isn't available
        </h1>
        <p className="text-sm">
          {q.error instanceof Error ? q.error.message : "This link has expired or been withdrawn."}
        </p>
        <p className="mt-4 text-sm">Ask whoever sent it to you for a fresh one.</p>
      </Centered>
    );
  }

  if (!data) return <Centered>Loading…</Centered>;

  return (
    <div className="min-h-screen bg-neutral-100">
      <header className="sticky top-0 z-30 border-b border-neutral-200 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-[1100px] flex-wrap items-center gap-3 px-6 py-3">
          <div className="min-w-0 flex-1">
            <p className="truncate font-[family-name:--font-display] font-semibold text-neutral-900">
              {data.contract.title}
            </p>
            <p className="truncate text-neutral-500 text-xs">
              {data.contract.number}
              {identity.name ? ` · ${identity.name}` : ""}
            </p>
          </div>
          <Button variant="ghost" onClick={downloadDocx} disabled={exportingDocx}>
            {exportingDocx ? "Preparing…" : "Download Word"}
          </Button>
          <Button onClick={download} disabled={exporting || !fontsReady}>
            {exporting ? "Preparing…" : "Download PDF"}
          </Button>
        </div>
        {error && (
          <p className="border-red-200 border-t bg-red-50 px-6 py-2 text-red-700 text-sm">
            Couldn't create that download: {error}
          </p>
        )}
      </header>

      <main className="mx-auto max-w-[1100px] px-4 py-6">
        <PagedFrame pageTops={pageTops}>
          <DocumentView
            ref={docRef}
            kind={data.contract.kind}
            contract={data.contract}
            clientName={data.client?.name ?? ""}
            identity={identity}
            palette={palette}
            typography={typography}
            sections={sections}
            segments={segments}
            resolvedByKey={resolvedByKey}
          />
        </PagedFrame>
      </main>
    </div>
  );
}
